import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import styled from 'styled-components';
import { isPlayerSupported } from 'amazon-ivs-player';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { isVideo } from '../../../utils/utils';
import MediaVideo from '../../general-components/media-video';
import { getVideoStill } from '../../../utils/media';
import { S3_SUB_DIRECTORY } from '../../../connection/media';
import { GreenroomState as GS } from '../../../stores/types/types';
import { OnsiteLocation } from '../../../stores/reducers/video';
import { RoomStates } from '../../../types/enums';

interface Props {
	showHoldSlide: boolean;
	showVideoWrapper?: boolean;
}

const HoldWrapper = styled.div`
	position: absolute;
	inset: 0;
	display: flex;
	justify-content: center;
	aspect-ratio: 16/9;
	margin: auto;
	z-index: 1;
	background: #09090A;

	img {
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	&.fill {
		img {
			object-fit: cover;
		}

        .media-video-container {
            max-width: 100%;
            max-height: 100%;
        }

        .media-video,
        .media-img,
        .media-video-container,
        .media-video-poster img {
            object-fit: cover;
        }
    }
`;

export default function HoldSlide({ showHoldSlide, showVideoWrapper }: Props) {
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const onsiteLocationSession = useSelector((s: GS) => s.VideoReducer.onsiteLocationSession);
	const onsiteLocations = useSelector((s: GS) => s.VideoReducer.onsiteLocations);
	const recentSlideDeckEntity = useSelector((s: GS) => s.MediaReducer.recentSlideDeckEntity);
	const media = useSelector((s: GS) => s.MediaReducer.media);
	const presentedMedia = useSelector((s: GS) => s.MediaReducer.presentedMedia);
	const mediaPages = useSelector((s: GS) => s.MediaReducer.mediaPages);

	const [showBackground, setShowBackground] = useState<boolean>(false);

	const video = useRef<HTMLVideoElement | null>(null);

	// uploaded hold slide

	const sessionHoldSlide = useMemo(
		() => onsiteLocations.find((l) => l.id === onsiteLocationSession?.id)?.holdSlide ?? '',
		[onsiteLocationSession, onsiteLocations],
	);

	// most recent slide deck

	const useSlideDeck = useMemo(
		() => (onsiteLocations.find(
			(ls: OnsiteLocation) => ls.id === onsiteLocationSession?.id,
		)?.useSlideDeck && roomState === RoomStates.live) ?? false,
		[onsiteLocations, onsiteLocationSession],
	);

	const recentSlideDeck = useMemo(
		() => media.find((m) => m.id === recentSlideDeckEntity?.entityValue.mediaId),
		[media, recentSlideDeckEntity],
	);

	const recentSlideDeckPresentedMedia = useMemo(
		() => presentedMedia.find((m) => m.mediaId === recentSlideDeck?.id),
		[presentedMedia, recentSlideDeck],
	);

	// smart canvas media url taken from either type

	const mediaSrc = useMemo(() => {
		if (roomState !== RoomStates.showbuilder && useSlideDeck && recentSlideDeckPresentedMedia) {
			const { currentPage } = recentSlideDeckPresentedMedia;
			const imageArray = recentSlideDeck?.media_data.image_array ?? [''];
			const currentPageUrl = imageArray[currentPage - 1] ?? '';
			return currentPageUrl;
		}
		return sessionHoldSlide;
	}, [
		roomState,
		useSlideDeck,
		recentSlideDeck,
		recentSlideDeckPresentedMedia?.currentPage,
	]);

	// video state

	const recentSlideDeckCurrentPage = useMemo(() => {
		if (useSlideDeck && recentSlideDeckPresentedMedia && mediaSrc) {
			return mediaPages.find((m) => m.url === mediaSrc) ?? null;
		}
		return null;
	}, [
		useSlideDeck,
		recentSlideDeckPresentedMedia,
		mediaSrc,
		mediaPages,
	]);

	useEffect(() => {
		if (video.current && useSlideDeck) {
			// apply presented media state to video element if from recent slide deck
			if (recentSlideDeckPresentedMedia?.videoState) {
				video.current.currentTime = recentSlideDeckPresentedMedia.videoState?.timeElapsed ?? 0;
			}

			if (recentSlideDeckCurrentPage) {
				video.current.volume = recentSlideDeckCurrentPage.volume;
				video.current.muted = recentSlideDeckCurrentPage.muted;
			}
		} else if (video.current) {
			// uploaded hold slide videos should always have a volume of 0
			video.current.volume = 0;
			video.current.muted = true;
		}
	}, [
		video.current,
		useSlideDeck,
		recentSlideDeckPresentedMedia?.videoState,
		recentSlideDeckCurrentPage?.volume,
		recentSlideDeckCurrentPage?.muted,
	]);

	useEffect(() => {
		// play and pause on shot change
		if (video.current && showHoldSlide) {
			if (useSlideDeck && recentSlideDeckPresentedMedia?.videoState) {
				let newTimeElapsed = recentSlideDeckPresentedMedia.videoState?.timeElapsed;

				// if video was last playing, calculate time elapsed since starting
				if (
					recentSlideDeckPresentedMedia.videoState?.timeStarted
					&& !recentSlideDeckPresentedMedia.videoState?.paused
				) {
					const timeStart = recentSlideDeckPresentedMedia.videoState?.timeStarted.valueOf();
					newTimeElapsed = ((new Date().valueOf() - timeStart) / 1000);
				}

				video.current.currentTime = newTimeElapsed ?? 0;

				if (recentSlideDeckPresentedMedia?.videoState?.paused) {
					video.current.pause();
				} else {
					video.current.play();
				}
			} else {
				// uploaded hold slide videos should always play from the start
				video.current.currentTime = 0;
				video.current.play();
			}
		} else if (video.current) {
			video.current.pause();
		}
	}, [video.current, useSlideDeck, showHoldSlide]);

	return (
		showVideoWrapper ? (
			<div id="video-wrapper" />
		) : (
			<HoldWrapper
				className={classNames(useSlideDeck && recentSlideDeck?.media_data.fill && 'fill')}
				style={{
					display: showHoldSlide ? 'flex' : 'none',
					background: useSlideDeck && showBackground
						? recentSlideDeck?.media_data.background_color
						: '',
				}}
			>
				{mediaSrc && (
					isVideo(mediaSrc) ? (
						<MediaVideo
							className="media-video"
							src={mediaSrc}
							isHlsVideo={mediaSrc.endsWith('.m3u8') && isPlayerSupported}
							crossOrigin={undefined}
							ref={video}
							loop={
								useSlideDeck && typeof recentSlideDeck?.media_data.loop === 'boolean'
									? recentSlideDeck?.media_data.loop
									: true
							}
							muted={
								useSlideDeck && typeof recentSlideDeckCurrentPage?.muted === 'boolean'
									? recentSlideDeckCurrentPage?.muted
									: true
							}
							duration={video.current?.duration ?? 0}
							elapsed={video.current?.currentTime ?? 0}
							onCanPlayThrough={() => setShowBackground(true)}
							onPause={(e) => {
								if (!useSlideDeck) {
									e.currentTarget.play();
								}
							}}
							posterImage={getVideoStill(mediaSrc, S3_SUB_DIRECTORY.POSTER)}
						/>
					) : (
						<img
							src={mediaSrc}
							alt="Hold Slide"
							onLoad={() => setShowBackground(true)}
						/>
					)
				)}
			</HoldWrapper>
		)
	);
}
